import { ReactElement, PropsWithChildren } from 'react';
import {
  IFragmentCtaFields,
  IMetaLocationClosedDates,
  IPageLocationFields,
} from 'types/contentful';
import { format, isSameWeek } from 'date-fns';

import MapComponent from '../Map';
import CTA from '../Cta';
import ScheduleAppointmentCta from '../ScheduleAppointmentCta';
import { formatPhoneNumber } from 'lib/util';
// import MapMarker from './MapMarker';
// import LocationMarkerSvg from './MapMarker/LocationMarkerSvg';

type Hours = {
  open: string;
  close: string;
};

export type OfficeHours = {
  [Sunday: string]: Hours | Hours[];
  Monday: Hours | Hours[];
  Tuesday: Hours | Hours[];
  Wednesday: Hours | Hours[];
  Thursday: Hours | Hours[];
  Friday: Hours | Hours[];
  Saturday: Hours | Hours[];
};

interface OfficeDetailsProps {
  siteName: string;
  officeName: string;
  hideEyeOnPin: boolean;
  hours: OfficeHours;
  hoursNotes?: string;
  backgroundColor?: 'primary' | 'secondary' | 'tertiary' | 'muted' | 'neutral';
  center: {
    lat: number;
    lng: number;
  };
  closedDates: IMetaLocationClosedDates | undefined;
  cta?: IFragmentCtaFields[] | undefined;
  location?: IPageLocationFields | undefined;

  /** Marker Pin Icon Type */
  markerIcon: string;
  officeInfo: IPageLocationFields;
  zoom?: number;
  factua?: boolean;
  providerId?: string;
}

interface ClosedDate {
  key: string;
  value: string;
  day?: string;
}

// used in order to keep the proper display order of days in loop
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const checkClosedDates = (closedDates: IMetaLocationClosedDates | undefined): ClosedDate[] => {
  if (!closedDates) return [];
  const dates = closedDates.fields.dates as ClosedDate[];
  const today = new Date();

  return dates
    .filter((date) => isSameWeek(new Date(date.value), today))
    .map((date) => {
      return {
        ...date,
        day: DAYS[new Date(date.value).getDay()],
      };
    })
    .sort((a, b) => DAYS.indexOf(a.day) - DAYS.indexOf(b.day));
};

export default function DoctorOfficeDetails({
  // siteName,
  officeName,
  hours,
  hideEyeOnPin,
  hoursNotes,
  children,
  center,
  backgroundColor,
  closedDates,
  markerIcon = 'Eye-V01',
  cta,
  location,
  zoom = 4,
  factua = false,
  providerId,
}: PropsWithChildren<OfficeDetailsProps>): ReactElement {
  const closedList = checkClosedDates(closedDates);

  const locationAddressArray = [
    location?.address1,
    location?.address2,
    location?.address3,
    location?.city,
    location?.state,
    location?.zipCode,
  ];
  const locationAddress = locationAddressArray.filter(Boolean).join(', ');

  return (
    <div
      id="my_locations"
      className={`flex flex-col lg:flex-row p-4 md:px-10 lg:px-16 lg:pb-10 w-full lg:bg-${backgroundColor}`}
      style={{ scrollMarginTop: '13rem' }}
    >
      <div className="w-full lg:w-2/3 text-lg">
        <div className="flex flex-col md:flex-row md:gap-10 lg:gap-8">
          <div className="md:w-5/12">
            <h3 className="text-lg font-thin mb-4">Contact Information</h3>
            <div className="block  font-light">
              <span className="md:block">{locationAddress}</span>
            </div>

            {location?.phoneNumber && (
              <div className="mt-2">
                <a
                  href={`tel:${location.phoneNumber}`}
                  className="block font-light text-secondary-actual"
                >
                  {formatPhoneNumber(location.phoneNumber)}
                </a>
              </div>
            )}

            <div className="mt-8">
              {cta && location && (
                <>
                  {cta.map((x, i) => (
                    <div key={i} className="flex flex-col gap-2 max-w-400">
                      <ScheduleAppointmentCta
                        location={location}
                        colorScheme="Primary"
                        wrapperClasses=""
                        classNames="min-w-cta w-full"
                        factua={factua}
                        providerId={providerId}
                        from="doctor"
                      />
                      <CTA key={i} {...x} classNames="" />
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-center md:w-6/12 pt-8 md:pt-0">
            {closedList.length > 0 && (
              <div className="w-full mt-6 mb-6 text-red">
                We are closed for
                {closedList
                  .map(
                    (closed) => ` ${closed.key} (${format(new Date(closed.value), 'EEE, LLL do')})`
                  )
                  .join(' and ')}
                .
              </div>
            )}

            <div className="w-full md:mx-4">
              <h3 className="text-lg font-thin mb-4">Hours of Operation</h3>
              {DAYS.map((day, i) => (
                <OfficeHours key={i} day={day} hours={hours[day]} closedList={closedList} />
              ))}
              {hoursNotes && (
                <span className="mt-3 block text-center text-secondary">{hoursNotes}</span>
              )}
            </div>
          </div>
        </div>
        {children}
      </div>
      <div className="hidden md:block lg:w-1/3" style={{ height: '430px' }}>
        <MapComponent
          defaultCenter={center}
          iconDetails={{
            title: officeName,
            hideEyeOnPin,
            classNames: 'text-primary-actual',
            focused: true,
            markerIcon,
          }}
          userLocation={{
            map: { lat: center.lat, lng: center.lng },
          }}
          mapOptions={{
            fullscreenControl: false,
            zoomControl: false,
            unBlockOnClick: false,
            zoom: zoom,
          }}
          setActiveLocation={() => {
            return null;
          }}
        />
      </div>
    </div>
  );
}

function OfficeHours({
  day,
  hours,
  closedList,
}: {
  day: string;
  hours: Hours | Hours[];
  closedList: ClosedDate[];
}): ReactElement {
  const forceClosed = closedList.find((closed) => closed.day === day);

  const hoursElem = forceClosed ? (
    <Closed />
  ) : Array.isArray(hours) ? (
    <div className="flex flex-col items-end text-right">
      {hours.map((hours, i) => (
        <Open key={i} from={hours.open} to={hours.close} />
      ))}
    </div>
  ) : hours?.open && hours?.close ? (
    <Open from={hours.open} to={hours.close} />
  ) : (
    <Closed />
  );

  return (
    <div
      className={`flex items-center justify-between py-2 border-b border-gray-200 ${
        forceClosed ? 'text-red' : 'text-primary-actual'
      }`}
    >
      <span className="">{day}</span>
      {hoursElem}
    </div>
  );
}

function Closed(): ReactElement {
  return <span className="text-right">Closed</span>;
}

function Open({ from, to }: { from: string; to: string }): ReactElement {
  return (
    <span className="text-right">
      {from} - {to}
    </span>
  );
}
